import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  SelectChangeEvent,
  Button,
  Tooltip,
} from '@mui/material';
import { Search as SearchIcon, RestartAlt as ResetIcon } from '@mui/icons-material';

export interface FilterOption {
  value: string | number;
  label: string;
}

interface LibraryFilterPanelProps {
  searchQuery: string;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  
  // Main category filter (Category for Food/Workout, Form for Medication)
  mainFilterValue: string;
  onMainFilterChange: (event: SelectChangeEvent<string>) => void;
  mainFilterOptions: { value: string; label: string }[];
  mainFilterLabel: string;

  // Source filter
  sourceFilterValue: string;
  onSourceFilterChange: (event: SelectChangeEvent<string>) => void;
  sourceFilterOptions?: { value: string; label: string }[];
  
  // Expert filter
  expertFilterValue: number | '';
  onExpertFilterChange: (event: SelectChangeEvent<number | ''>) => void;
  expertOptions: { id: number; name: string }[];

  // Structured filter
  structuredFilterValue: string;
  onStructuredFilterChange: (event: SelectChangeEvent<string>) => void;

  // Reset filters
  onResetFilters: () => void;

  // Optional extra filters (like Dosage for Medications)
  children?: React.ReactNode;
}

export const LibraryFilterPanel: React.FC<LibraryFilterPanelProps> = ({
  searchQuery,
  onSearchChange,
  mainFilterValue,
  onMainFilterChange,
  mainFilterOptions,
  mainFilterLabel,
  sourceFilterValue,
  onSourceFilterChange,
  sourceFilterOptions,
  expertFilterValue,
  onExpertFilterChange,
  expertOptions,
  structuredFilterValue,
  onStructuredFilterChange,
  onResetFilters,
  children,
}) => {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
      <TextField
        label="Пошук"
        value={searchQuery}
        onChange={onSearchChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ flexGrow: 2, minWidth: '200px' }}
        size="small"
      />

      <FormControl sx={{ minWidth: '150px', flexGrow: 1 }} size="small">
        <InputLabel>{mainFilterLabel}</InputLabel>
        <Select value={mainFilterValue} label={mainFilterLabel} onChange={onMainFilterChange}>
          <MenuItem value=""><em>Всі</em></MenuItem>
          {mainFilterOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {children}

      <FormControl sx={{ minWidth: '150px', flexGrow: 1 }} size="small">
        <InputLabel>Джерело</InputLabel>
        <Select value={sourceFilterValue} label="Джерело" onChange={onSourceFilterChange}>
          {sourceFilterOptions ? (
            sourceFilterOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))
          ) : (
            [
              <MenuItem key="all" value="all">Всі</MenuItem>,
              <MenuItem key="system" value="system">Система</MenuItem>,
              <MenuItem key="user" value="user">Користувач</MenuItem>
            ]
          )}
        </Select>
      </FormControl>

      <FormControl sx={{ minWidth: '150px', flexGrow: 1 }} size="small">
        <InputLabel>Експерт</InputLabel>
        <Select value={expertFilterValue} label="Експерт" onChange={onExpertFilterChange}>
          <MenuItem value=""><em>Всі</em></MenuItem>
          {expertOptions.map(e => (
            <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ minWidth: '150px', flexGrow: 1 }} size="small">
        <InputLabel>Структурованість</InputLabel>
        <Select value={structuredFilterValue} label="Структурованість" onChange={onStructuredFilterChange}>
          <MenuItem value="all"><em>Всі</em></MenuItem>
          <MenuItem value="true">Структуровані</MenuItem>
          <MenuItem value="false">Неструктуровані</MenuItem>
        </Select>
      </FormControl>

      <Tooltip title="Скинути всі фільтри">
        <Button 
          variant="outlined" 
          color="secondary" 
          onClick={onResetFilters}
          startIcon={<ResetIcon />}
          sx={{ height: '40px' }}
        >
          Скинути
        </Button>
      </Tooltip>
    </Box>
  );
};
